/**
 * World Map screen — overview of all 10 MVP letters as tappable islands.
 *
 * Groups letters by `island_group` so the visual arrangement hints at the
 * traditional Arabic alphabet ordering without being a strict grid. For
 * M2 the layout is a responsive grid; M6 will wrap this in a scrollable
 * illustrated map with an SVG path connecting the islands.
 */

import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { letters, mapState, contentError, loadContent, refreshMap } from '../state/content';
import { LetterTile } from '../components/LetterTile';
import { MapSkeleton } from '../components/Skeleton';
import { NavButton } from '../components/NavButton';

export function WorldMap() {
  const { route } = useLocation();

  useEffect(() => {
    // Load the static letter corpus once per session, but ALWAYS refetch
    // map state when we enter the Map screen — stars earned in a stage
    // should appear the moment the child returns to the map.
    void loadContent();
    void refreshMap();
  }, []);

  const ls = letters.value;
  const ms = mapState.value;

  if (contentError.value) {
    return <ErrorState message={contentError.value} />;
  }

  if (!ls || !ms) {
    return <Loading />;
  }

  // Map letter_id -> island status from /api/map
  const statusById = new Map(
    ms.islands.map((i) => [i.letter_id, { status: i.status, stars: i.stars }] as const),
  );

  // Group by island_group
  const groups = new Map<number, typeof ls>();
  for (const l of ls) {
    if (!groups.has(l.island_group)) groups.set(l.island_group, []);
    groups.get(l.island_group)!.push(l);
  }

  return (
    <div class="flex-1 px-4 py-6 pb-24 max-w-3xl mx-auto w-full">
      <header class="flex items-center justify-between mb-6 px-2">
        <div>
          <h1 class="text-3xl font-display text-coral leading-none">الخَرِيطَةُ</h1>
          <p class="text-[10px] font-body text-ink/50 mt-0.5" dir="ltr">
            Map — tap a letter to open
          </p>
        </div>
        <div class="flex gap-3 text-sm font-body">
          <span class="bg-white/80 rounded-full px-3 py-1 shadow-sm">🔥 {ms.streak}</span>
          <span class="bg-white/80 rounded-full px-3 py-1 shadow-sm">⭐ {ms.totalStars}</span>
        </div>
      </header>

      <div class="space-y-6">
        {[...groups.entries()]
          .sort(([a], [b]) => a - b)
          .map(([group, letters]) => (
            <section key={group}>
              <h2 class="text-xs font-body text-ink/50 mb-2 px-2" dir="ltr">
                Island group {group}
              </h2>
              <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {letters.map((l) => {
                  const s = statusById.get(l.id) ?? { status: 'locked' as const, stars: 0 };
                  return (
                    <LetterTile
                      key={l.id}
                      letter={l.letter}
                      nameAr={l.name_ar}
                      nameEn={l.name_en}
                      status={s.status}
                      stars={s.stars}
                      onClick={() => route(`/island/${l.id}`)}
                    />
                  );
                })}
              </div>
            </section>
          ))}
      </div>

      <footer class="mt-10 grid grid-cols-3 gap-3">
        <NavButton
          ar="الكِتَابُ"
          en="Letter book"
          trailing="📖"
          onClick={() => route('/collection')}
        />
        <NavButton
          ar="البَيْتُ"
          en="Home"
          onClick={() => route('/')}
        />
        <NavButton
          ar="الوَالِدَان"
          en="Parents"
          trailing="👨‍👩‍👧"
          onClick={() => route('/parent')}
        />
      </footer>
    </div>
  );
}

function Loading() {
  return <MapSkeleton />;
}

function ErrorState({ message }: { message: string }) {
  return (
    <div class="flex-1 grid place-items-center p-6 text-center">
      <div>
        <p class="text-4xl mb-4">🐻</p>
        <p class="text-2xl font-display">حَاوِلْ مَرَّةً أُخْرَى!</p>
        <p class="text-xs text-ink/40 mt-3" dir="ltr">
          {message}
        </p>
      </div>
    </div>
  );
}
